"use client"

import React, { useCallback } from 'react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Badge } from './badge'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from './breadcrumb'
import { useS3Picker, S3PickerProps, S3Object } from '../../hooks/use-s3-picker'
import { 
  Folder, 
  File, 
  ArrowUp, 
  Home, 
  Loader2, 
  Check, 
  AlertCircle,
  RefreshCw
} from 'lucide-react'

/**
 * S3-compatible file picker component with aurora design
 */
export function S3FilePicker({
  config,
  initialPrefix = '',
  multiselect = false,
  allowedExtensions,
  mimeTypes,
  showFolders = true,
  pageSize = 100,
  showFileSize = true,
  showLastModified = true,
  onFilesSelected,
  onSelectionChange,
  isLoading: externalLoading = false,
  className,
  disabled = false
}: S3PickerProps) {
  const { state, actions } = useS3Picker({
    config,
    initialPrefix,
    multiselect,
    allowedExtensions,
    mimeTypes,
    showFolders,
    pageSize
  })

  const isLoading = state.isLoading || externalLoading

  // Handle file/folder click
  const handleItemClick = useCallback((object: S3Object) => {
    if (disabled || isLoading) return

    if (object.type === 'folder') {
      actions.navigateToFolder(object.key)
    } else {
      actions.toggleFileSelection(object)
    }
  }, [disabled, isLoading, actions])

  // Handle selection confirmation
  const handleConfirmSelection = useCallback(() => {
    if (state.selectedFiles.length > 0) {
      onFilesSelected(state.selectedFiles)
    }
  }, [state.selectedFiles, onFilesSelected])

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-'
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unitIndex = 0
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
  }

  // Format date
  const formatDate = (date?: Date): string => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  // Check if file is selected
  const isFileSelected = (file: S3Object): boolean => {
    return state.selectedFiles.some(f => f.key === file.key)
  }

  return (
    <div className={cn(
      "flex flex-col bg-card border border-border rounded-lg shadow-sm overflow-hidden",
      "dark:bg-card/50 dark:border-border/70",
      className
    )}>
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between p-4 border-b border-border dark:border-border/70">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Breadcrumb>
            <BreadcrumbList>
              {state.breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.prefix}>
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      onClick={() => !disabled && !isLoading && actions.navigateToFolder(crumb.prefix)}
                      className={cn(
                        "cursor-pointer hover:text-aurora-blue transition-colors",
                        (disabled || isLoading) && "cursor-not-allowed opacity-50"
                      )}
                    >
                      {index === 0 ? <Home className="w-4 h-4" /> : crumb.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {index < state.breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        
        <div className="flex items-center gap-2">
          {state.currentPrefix && (
            <Button
              variant="ghost"
              size="sm"
              onClick={actions.navigateUp}
              disabled={disabled || isLoading}
              className="text-muted-foreground hover:text-aurora-blue"
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={actions.refresh}
            disabled={disabled || isLoading}
            className="text-muted-foreground hover:text-aurora-blue"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0">
        {/* Error state */}
        {state.error && (
          <div className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-aurora-red mb-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Error loading files</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{state.error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={actions.refresh}
              disabled={disabled || isLoading}
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Loading state */}
        {isLoading && state.objects.length === 0 && !state.error && (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-aurora-blue" />
            <p className="text-muted-foreground">Loading files...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !state.error && state.objects.length === 0 && (
          <div className="p-8 text-center">
            <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">This folder is empty</p>
          </div>
        )}

        {/* File list */}
        {state.objects.length > 0 && (
          <div className="divide-y divide-border dark:divide-border/70">
            {state.objects.map((object) => (
              <div
                key={object.key}
                onClick={() => handleItemClick(object)}
                className={cn(
                  "flex items-center gap-3 p-3 transition-all duration-200 cursor-pointer",
                  "hover:bg-aurora-blue/10 dark:hover:bg-aurora-blue/20",
                  object.type === 'file' && isFileSelected(object) && 
                    "bg-aurora-teal/10 text-aurora-teal dark:bg-aurora-teal/20",
                  (disabled || isLoading) && "cursor-not-allowed opacity-50"
                )}
              >
                {/* Icon */}
                <div className="flex-shrink-0">
                  {object.type === 'folder' ? (
                    <Folder className="w-5 h-5 text-aurora-blue" />
                  ) : (
                    <div className="relative">
                      <File className="w-5 h-5 text-aurora-purple" />
                      {object.type === 'file' && isFileSelected(object) && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-aurora-teal rounded-full flex items-center justify-center">
                          <Check className="w-2 h-2 text-white" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{object.name}</p>
                  {object.type === 'folder' && (
                    <p className="text-xs text-muted-foreground">Folder</p>
                  )}
                </div>

                {/* File details */}
                {object.type === 'file' && (
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {showFileSize && (
                      <span>{formatFileSize(object.size)}</span>
                    )}
                    {showLastModified && (
                      <span>{formatDate(object.lastModified)}</span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Load more button */}
            {state.hasMore && (
              <div className="p-4 text-center border-t border-border dark:border-border/70">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={actions.loadMore}
                  disabled={disabled || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with selection info and actions */}
      {(multiselect || state.selectedFiles.length > 0) && (
        <div className="p-4 border-t border-border dark:border-border/70 bg-muted/30 dark:bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {state.selectedFiles.length > 0 ? (
                <Badge variant="outline" className="bg-aurora-teal/10 text-aurora-teal border-aurora-teal/30">
                  {state.selectedFiles.length} selected
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {multiselect ? 'Select multiple files' : 'Select a file'}
                </span>
              )}
              
              {allowedExtensions && allowedExtensions.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {allowedExtensions.join(', ')}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              {state.selectedFiles.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={actions.clearSelection}
                  disabled={disabled || isLoading}
                >
                  Clear
                </Button>
              )}
              
              <Button
                variant="default"
                size="sm"
                onClick={handleConfirmSelection}
                disabled={disabled || isLoading || state.selectedFiles.length === 0}
              >
                Select {state.selectedFiles.length > 0 ? `(${state.selectedFiles.length})` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}